import { Add as AddIcon, InfoOutlined as InfoOutlinedIcon, OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import { GridCellParams, GridColDef, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import { FC, useMemo, useState } from "react";

import { AGDataGrid } from "../../../components/AGDataGrid";
import TableSearchInput from "../../../components/TableSearchInput";
import { useGetInterceptorsListQuery } from "../../../redux/reducers/configuration.reducer";
import { IGetInterceptorsListRequestData, INTERCEPTOR_STATUS_E } from "../../../types";
import { ROWS_PER_PAGE } from "../../../utils/constants";
import InterceptorDetailsDialog from "./InterceptorDetailsDialog";
import CameraSettingsDialog from "./CameraSettingsDialog";
import DeviceSettingsDialog from "./DeviceSettingsDialog";
import "../index.css";
import { useAppSelector } from "../../../redux";

export interface ISystemTemperatureDetails {
	temperature: string;
	current: string;
	maximum: string;
}

export interface IMemoryUsageDetails {
	type: string;
	current: string;
	total: string;
}

export type SoftwareStatusType = "running" | "stopped" | "unknown";

export interface ISoftwareStatusDetails {
	software: string;
	status: SoftwareStatusType;
	uptime: string;
}

export interface IInterceptorRowDetails {
	id: number;
	sr_no: number;
	location_name: string;
	number_plate: string;
	uuid: string;
	is_active: INTERCEPTOR_STATUS_E;
	device_id: number;
	device_name: string;
	device_serial_number: string;
	device_system_version: string;
	device_local_ip: string;
	device_global_ip: string;
	system_temperature: ISystemTemperatureDetails[];
	memory_usage: IMemoryUsageDetails[];
	software_status: ISoftwareStatusDetails[];
	rto: number;
}

const Interceptors: FC = () => {
	const authState = useAppSelector((state) => state.auth);

	const [filters, setFilters] = useState<IGetInterceptorsListRequestData>({ page: 1 });
	const [showInterceptorDetailsDialog, setShowInterceptorDetailsDialog] = useState(false);
	const [selectedInterceptorDetails, setSelectedInterceptorDetails] = useState<IInterceptorRowDetails | null>(null);
	const [showDeviceSettingsDialog, setShowDeviceSettingsDialog] = useState<boolean>(false);
	const [showCameraSettingsDialog, setShowCameraSettingsDialog] = useState<boolean>(false);

	// GET INTERCEPTORS LIST
	const {
		data: getInterceptorsListResponse,
		isLoading: getInterceptorsListLoading,
		isFetching: getInterceptorsListFetching,
		refetch: refetchInterceptorsList
	} = useGetInterceptorsListQuery(filters);

	const readOnlyPermission = useMemo<boolean>(
		() => !authState.user.permissions || !authState.user.permissions.interceptor_vehicles?.edit,
		[authState.user.permissions]
	);

	const rowsData = useMemo<IInterceptorRowDetails[]>(() => {
		if (!getInterceptorsListResponse) return [];

		return getInterceptorsListResponse.results.map((item, index) => {
			const temperatureDetails: ISystemTemperatureDetails[] = [];
			const memoryUsageDetails: IMemoryUsageDetails[] = [];
			const softwareStatusDetails: ISoftwareStatusDetails[] = [];

			if (item.device_data?.health_data?.device_data) {
				const deviceStatus = item.device_data.health_data.device_data;

				if (deviceStatus.detail[1] && deviceStatus.detail[1].GPU) {
					temperatureDetails.push({
						temperature: "MCPU",
						current: `${deviceStatus.detail[1]?.GPU[0] ?? 0}\u00b0C`,
						maximum: `${deviceStatus.detail[1]?.GPU[1] ?? 0}\u00b0C`
					});
				}

				temperatureDetails.push({
					temperature: "Thermal",
					current:
						deviceStatus.detail[1] && deviceStatus.detail[1].Thermal
							? `${deviceStatus.detail[1].Thermal[0] ?? 0}\u00b0C`
							: "N/A",
					maximum:
						deviceStatus.detail[1] && deviceStatus.detail[1].Thermal
							? `${deviceStatus.detail[1].Thermal[1] ?? 0}\u00b0C`
							: "N/A"
				});

				memoryUsageDetails.push({
					type: "RAM",
					current:
						deviceStatus.detail[2] && deviceStatus.detail[2].Ram
							? `${deviceStatus.detail[2].Ram[0]?.toFixed(1) ?? 0} GB`
							: "N/A",
					total:
						deviceStatus.detail[2] && deviceStatus.detail[2].Ram
							? `${deviceStatus.detail[2].Ram[1]?.toFixed(1) ?? 0} GB`
							: "N/A"
				});

				memoryUsageDetails.push({
					type: "SWAP",
					current:
						deviceStatus.detail[2] && deviceStatus.detail[2].Swap
							? `${deviceStatus.detail[2].Swap[0]?.toFixed(1) ?? 0} GB`
							: "N/A",
					total:
						deviceStatus.detail[2] && deviceStatus.detail[2].Swap
							? `${deviceStatus.detail[2].Swap[1]?.toFixed(1) ?? 0} GB`
							: "N/A"
				});

				memoryUsageDetails.push({
					type: "Device",
					current:
						deviceStatus.detail[2] && deviceStatus.detail[2].Device
							? `${deviceStatus.detail[2].Device[0]?.toFixed(1) ?? 0} GB`
							: "N/A",
					total:
						deviceStatus.detail[2] && deviceStatus.detail[2].Device
							? `${deviceStatus.detail[2].Device[1]?.toFixed(1) ?? 0} GB`
							: "N/A"
				});

				if (deviceStatus.detail[2] && deviceStatus.detail[2].External) {
					memoryUsageDetails.push({
						type: "External",
						current: `${deviceStatus.detail[2]?.External[0]?.toFixed(1) ?? 0} GB`,
						total: `${deviceStatus.detail[2]?.External[1]?.toFixed(1) ?? 0} GB`
					});
				}
			}

			if (item.device_data?.health_data?.docker_data) {
				const systemStatus = item.device_data.health_data.docker_data;

				softwareStatusDetails.push(
					{
						software: "Software 1",
						status: systemStatus.detail.db
							? ((systemStatus.detail.db[0] ?? "stopped") as SoftwareStatusType)
							: "unknown",
						uptime: systemStatus.detail.db?.[1] ?? ""
					},
					{
						software: "Software 2",
						status: systemStatus.detail.gg_monitor
							? ((systemStatus.detail.gg_monitor[0] ?? "stopped") as SoftwareStatusType)
							: "unknown",
						uptime: systemStatus.detail.gg_monitor?.[1] ?? ""
					},
					{
						software: "Software 3",
						status: systemStatus.detail.ml_engine
							? ((systemStatus.detail.ml_engine[0] ?? "stopped") as SoftwareStatusType)
							: "unknown",
						uptime: systemStatus.detail.ml_engine?.[1] ?? ""
					},
					{
						software: "Software 4",
						status: systemStatus.detail.nginx
							? ((systemStatus.detail.nginx[0] ?? "stopped") as SoftwareStatusType)
							: "unknown",
						uptime: systemStatus.detail.nginx?.[1] ?? ""
					},
					{
						software: "Software 5",
						status: systemStatus.detail.web
							? ((systemStatus.detail.web[0] ?? "stopped") as SoftwareStatusType)
							: "unknown",
						uptime: systemStatus.detail.web?.[1] ?? ""
					}
				);
			}

			return {
				id: item.id,
				sr_no: (filters.page - 1) * ROWS_PER_PAGE + index + 1,
				location_name: item.location_name ?? "",
				number_plate: item.numberplate,
				uuid: item.uuid,
				device_id: item.device,
				is_active: item.is_active,
				device_name: item.device_data?.device_name || "",
				device_serial_number: item.device_data?.device_uuid || "",
				device_system_version: item.device_data?.release_version || "",
				device_local_ip: item.device_data?.local_ipv4_address || "",
				device_global_ip: item.device_data?.global_ipv4_address || "",
				system_temperature: temperatureDetails,
				memory_usage: memoryUsageDetails,
				software_status: softwareStatusDetails,
				rto: item.device_data?.jurisdiction ?? 0
			};
		});
	}, [filters.page, getInterceptorsListResponse]);

	function handleSubmitSearchInput(searchInput: string) {
		setFilters((currentFilters) => {
			const updatedFilters: IGetInterceptorsListRequestData = { ...currentFilters };

			if (searchInput) {
				updatedFilters.search = searchInput;
			} else {
				delete updatedFilters.search;
			}

			return updatedFilters;
		});
	}

	function handleOpenDialog(rowDetails: IInterceptorRowDetails | null) {
		setSelectedInterceptorDetails(rowDetails);
		setShowInterceptorDetailsDialog(true);
	}

	function handleCloseDialog() {
		setShowInterceptorDetailsDialog(false);
		setSelectedInterceptorDetails(null);
		refetchInterceptorsList();
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFilters) => ({
			...currentFilters,
			page: event.page + 1
		}));
	}

	function handleOpenDetailsDialog(rowDetails: IInterceptorRowDetails, type: "device" | "camera") {
		setSelectedInterceptorDetails(rowDetails);
		if (type === "device") setShowDeviceSettingsDialog(true);
		else if (type === "camera") setShowCameraSettingsDialog(true);
	}

	function handleCloseDetailsDialog() {
		setShowDeviceSettingsDialog(false);
		setShowCameraSettingsDialog(false);
		setSelectedInterceptorDetails(null);
	}

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "#",
			width: 50,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "number_plate",
			headerName: "Interceptor Number Plate",
			flex: 1,
			minWidth: 200,
			sortable: false
		},
		{
			field: "uuid",
			headerName: "Serial Number",
			flex: 1,
			minWidth: 200,
			sortable: false
		},
		{
			field: "device_name",
			headerName: "Device Name",
			flex: 1,
			minWidth: 200,
			sortable: false
		},
		{
			field: "location_name",
			headerName: "Location",
			flex: 1,
			minWidth: 200,
			sortable: false
		},
		{
			field: "is_active",
			headerName: "Status",
			flex: 1,
			minWidth: 150,
			sortable: false,
			valueFormatter: (value: INTERCEPTOR_STATUS_E) =>
				value === INTERCEPTOR_STATUS_E.ONLINE
					? "Online"
					: value === INTERCEPTOR_STATUS_E.OFFLINE
					? "Offline"
					: "Unknown",
			cellClassName: (params: GridCellParams<IInterceptorRowDetails, INTERCEPTOR_STATUS_E>) =>
				`device-status ${
					params.value === INTERCEPTOR_STATUS_E.ONLINE
						? "Online"
						: params.value === INTERCEPTOR_STATUS_E.OFFLINE
						? "Offline"
						: "Unknown"
				}`
		},
		{
			field: "device_settings",
			headerName: "Device Settings",
			flex: 0.2,
			minWidth: 130,
			sortable: false,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<IInterceptorRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton size="small" onClick={() => handleOpenDetailsDialog(params.row, "device")}>
						<OpenInNewIcon fontSize="small" />
					</IconButton>
				</Box>
			)
		},
		{
			field: "camera_settings",
			headerName: "Camera Settings",
			flex: 0.2,
			minWidth: 130,
			sortable: false,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<IInterceptorRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton size="small" onClick={() => handleOpenDetailsDialog(params.row, "camera")}>
						<OpenInNewIcon fontSize="small" />
					</IconButton>
				</Box>
			)
		},
		{
			field: "actions",
			headerName: "",
			width: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IInterceptorRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton color="info" size="small" onClick={() => handleOpenDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	return (
		<Box className="page-content-wrapper">
			<Box className="page-header-wrapper">
				<TableSearchInput showClearButton={!!filters.search} onSubmit={handleSubmitSearchInput} />

				{!readOnlyPermission ? (
					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={() => handleOpenDialog(null)}
					>
						Add Interceptor
					</Button>
				) : null}
			</Box>

			<Box className="page-content">
				<AGDataGrid
					columns={columnsData}
					rows={rowsData}
					paginationMode="server"
					paginationModel={{ page: filters.page - 1, pageSize: ROWS_PER_PAGE }}
					onPaginationModelChange={handlePageChange}
					rowCount={getInterceptorsListResponse?.count ?? 0}
					loading={getInterceptorsListLoading || getInterceptorsListFetching}
					hideFooter={(getInterceptorsListResponse?.count ?? 0) <= ROWS_PER_PAGE}
				/>
			</Box>

			<InterceptorDetailsDialog
				open={showInterceptorDetailsDialog}
				interceptorDetails={selectedInterceptorDetails}
				onClose={handleCloseDialog}
			/>

			<CameraSettingsDialog
				open={showCameraSettingsDialog}
				deviceId={selectedInterceptorDetails?.device_id ?? -1}
				deviceName={selectedInterceptorDetails?.device_name ?? ""}
				onClose={handleCloseDetailsDialog}
			/>

			<DeviceSettingsDialog
				open={showDeviceSettingsDialog}
				deviceDetails={selectedInterceptorDetails}
				onClose={handleCloseDetailsDialog}
			/>
		</Box>
	);
};

export default Interceptors;
